import {
  BaseTag,
  ErrorContent,
  Flex,
  HitCounter,
  LinkText,
  Loading,
  Table,
  TablePageSizeSelector,
  TableRowMenuCell,
  Text,
  useTableRowSelection,
} from '@/components';
import { useCommentList } from '@/features/comment/hooks/comment';
import { useNavigateToPinpoint } from '@/features/comment/hooks/navigate-to-pinpoint';
import { usePinpointDisplayName } from '@/features/comment/hooks/pinpoint-display-name';
import { useCommentListSearchQueryStates } from '@/features/comment/hooks/search-query-states';
import { useCommentStackSiderView } from '@/features/comment/hooks/stack-sider-view';
import {
  CommentKeyLabelModifier,
  type CommentListElementModel,
} from '@/features/comment/models/comment';
import { TagTarget } from '@/features/tag/components';
import { useOrganizations } from '@/features/tenant-manager/organization/_hooks/organization';
import { useParseUsersByIds } from '@/features/tenant-manager/user/_hooks/user';
import {
  FALLBACK_TEXT,
  TABLE_DATE_WIDTH,
  TABLE_ROW_MENU_WIDTH,
  datetimeFormat,
  lowerCamelStringToSnake,
} from '@/utils';
import { isRowSelectionBlocked } from '@/utils';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { CommentUnreadBadge } from '../../common/CommentUnreadBadge';
import styles from './CommentListTable.module.scss';
import { CommentListTableBulkActionsDropdown } from './CommentListTableBulkActionsDropdown';

const CHECK_CIRCLE_SIZE = 20;

export const CommentListTable: FC = () => {
  const { t } = useTranslation();
  const [searchQueryStates, setSearchQueryStates] =
    useCommentListSearchQueryStates();
  const stackSiderView = useCommentStackSiderView();
  const { selectedRowKeys, setSelectedRowKeys } = useTableRowSelection();

  const { data: organizations } = useOrganizations();

  const comments = useCommentList({
    params: {
      ...searchQueryStates,
      ordering: searchQueryStates.ordering ?? undefined,
      resolved:
        searchQueryStates.resolvedStatus === 'all'
          ? undefined
          : searchQueryStates.resolvedStatus === 'resolved',
      publishType:
        searchQueryStates.publishType === 'open'
          ? undefined
          : searchQueryStates.publishType,
    },
  });
  const total = comments.data?.count ?? 0;
  const isSelecting = selectedRowKeys.length > 0;

  const firstStackSiderViewItem = stackSiderView.list.at(0);

  const userIdsForParse =
    comments.data?.results.reduce<string[]>((acc, comment) => {
      acc.push(comment.authorId);
      acc.push(...comment.assigneeIds);
      acc.push(...comment.relatedUserIds);
      return acc;
    }, []) ?? [];
  const { parseUserById, parseUsersByIds } =
    useParseUsersByIds(userIdsForParse);

  return (
    <Flex vertical gap="middle">
      <Flex justify="space-between" align="center">
        <Flex gap="middle" align="center">
          <HitCounter
            count={isSelecting ? selectedRowKeys.length : total}
            color={isSelecting ? 'green' : 'white'}
            label={isSelecting ? t('selecting') : t('relevant')}
          />
          <CommentListTableBulkActionsDropdown />
        </Flex>
        <TablePageSizeSelector
          defaultPageSize={searchQueryStates.pageSize}
          onChange={(newPageSize) => {
            setSearchQueryStates((prev) => ({
              ...prev,
              pageSize: newPageSize,
              page: 1,
            }));
          }}
        />
      </Flex>
      {comments.isError ? (
        <ErrorContent reloadable={false} />
      ) : comments.isPending ? (
        <Loading />
      ) : (
        <Table
          dataSource={comments.data?.results}
          rowKey="id"
          rowSelection={{
            columnWidth: 32,
            selectedRowKeys,
            preserveSelectedRowKeys: true,
            onChange: setSelectedRowKeys,
          }}
          pagination={{
            showSizeChanger: false,
            position: ['bottomRight'],
            total: comments.data?.count,
            current: searchQueryStates.page,
            pageSize: searchQueryStates.pageSize,
          }}
          onChange={(pagination, _, sorter, extra) => {
            switch (extra.action) {
              case 'paginate':
                if (pagination.current) {
                  setSearchQueryStates((prev) => ({
                    ...prev,
                    page: pagination.current,
                  }));
                }
                break;
              case 'sort': {
                if (Array.isArray(sorter)) {
                  return;
                }
                const prefix = sorter.order === 'descend' ? '-' : '';
                const orderingKey = lowerCamelStringToSnake(
                  sorter?.columnKey?.toString() ?? '',
                );

                setSearchQueryStates((prev) => ({
                  ...prev,
                  ordering: sorter.order ? `${prefix}${orderingKey}` : null,
                }));

                break;
              }
              default:
            }
          }}
          onRow={({ id }) => ({
            onClick: (e) => {
              if (isRowSelectionBlocked(e)) {
                return;
              }
              const newSelectedRowKeys = selectedRowKeys.includes(id)
                ? selectedRowKeys.filter((k) => k !== id)
                : [...selectedRowKeys, id];
              setSelectedRowKeys(newSelectedRowKeys);
            },
            className:
              firstStackSiderViewItem?.config.viewType === 'comment-details' &&
              firstStackSiderViewItem.config.id === id
                ? styles['selected-border']
                : undefined,
          })}
          columns={[
            {
              width: CHECK_CIRCLE_SIZE * 2,
              align: 'center',
              render: (record) => {
                return (
                  <BaseTag color={record.resolved ? 'green' : 'gray'}>
                    {record.resolved
                      ? t('comment.resolved')
                      : t('comment.unresolved')}
                  </BaseTag>
                );
              },
            },
            {
              title: t('comment.content'),
              key: 'commentContent',
              width: 180,
              render: (_, record) => (
                <Flex vertical>
                  {record.readStatus === 'unread' && <CommentUnreadBadge />}
                  <Text text={record.content} maxRows={3} weight="regular" />
                </Flex>
              ),
            },
            {
              title: t('tag'),
              key: 'tags',
              width: 100,
              render: (_, record) =>
                record.tagTargets.length
                  ? record.tagTargets.map((target) => (
                      <TagTarget tag={target.tag} key={target.tagId} />
                    ))
                  : FALLBACK_TEXT,
            },
            {
              title: t('organization-in-charge'),
              key: 'organization',
              width: 100,
              render: (_, record) => {
                return (
                  <Text
                    text={
                      record.objectAdditionalAttrs?.project?.organizationName
                    }
                    weight="regular"
                  />
                );
              },
            },
            {
              title: t('comment.target-name'),
              key: 'objectName',
              width: 250,
              render: (_, record) => <CommentTargetName record={record} />,
            },
            {
              title: t('last-updated-datetime'),
              key: 'lastModifiedAt',
              dataIndex: 'lastModifiedAt',
              sorter: true,
              width: TABLE_DATE_WIDTH,
              render: (_, record) =>
                datetimeFormat({
                  datetime: record.lastModifiedAt,
                  format: 'YYYY/MM/DD HH:mm',
                }),
            },
            {
              title: t('comment.author'),
              key: 'author',
              width: 180,
              render: (_, record) => parseUserById(record.authorId)?.name,
            },
            {
              title: t('comment.pinpoint-assignee'),
              key: 'assignees',
              width: 180,
              render: (_, record) => (
                <Text
                  text={
                    record.assigneeIds.length > 0
                      ? parseUsersByIds(record.assigneeIds)
                          .map((u) => u.name)
                          .join(', ')
                      : null
                  }
                  weight="regular"
                  maxRows={3}
                />
              ),
            },
            {
              title: t('viewable-organization'),
              key: 'sharedOrganizations',
              width: TABLE_DATE_WIDTH,
              render: (_, record) => {
                const sharedOrganizationNames = (organizations || [])
                  .filter(({ id }) => record.sharedOrganizationIds.includes(id))
                  .map(({ name }) => name);

                return (
                  <Text
                    text={sharedOrganizationNames?.join(', ') || null}
                    weight="regular"
                    maxRows={3}
                  />
                );
              },
            },
            {
              title: t('viewable-user'),
              key: 'relatedUserIds',
              width: 130,
              render: (_, record) => (
                <Text
                  text={
                    record.relatedUserIds.length > 0
                      ? parseUsersByIds(record.relatedUserIds)
                          .map((u) => u.name)
                          .join(', ')
                      : null
                  }
                  weight="regular"
                  maxRows={3}
                />
              ),
            },
            {
              title: t('comment.label'),
              key: 'label',
              width: TABLE_DATE_WIDTH,
              render: (_, record) => {
                return <Text text={record.label?.name} weight="regular" />;
              },
            },
            {
              title: '',
              key: 'actions',
              fixed: 'right',
              width: TABLE_ROW_MENU_WIDTH,
              render: (_, record) => (
                <TableRowMenuCell
                  onBrowse={() => {
                    stackSiderView.set({
                      payload: [
                        {
                          config: {
                            viewType: 'comment-details',
                            id: record.id,
                            target: {
                              projectFiscalYear:
                                record.objectAdditionalAttrs?.project
                                  ?.fiscalYear ?? undefined,
                            },
                          },
                          siderType: 'comment',
                        },
                      ],
                      target: {
                        type: 'project',
                        projectId: record.objectId,
                      },
                    });
                  }}
                />
              ),
            },
          ]}
        />
      )}
    </Flex>
  );
};

const CommentTargetName: FC<{
  record: z.infer<typeof CommentListElementModel>;
}> = ({ record }) => {
  const { t } = useTranslation();
  const navigateToPinpoint = useNavigateToPinpoint();
  const { label, isLoading } = usePinpointDisplayName(record.pinpoint);

  const targetName = record.pinpoint
    ? isLoading
      ? ''
      : label || t('comment.deleted-target')
    : undefined;

  const name = [
    record.objectAdditionalAttrs?.project?.fiscalYear,
    record.objectName,
    targetName,
  ]
    .filter(Boolean)
    .join(' | ');

  return (
    <Flex vertical>
      {record.objectName ? (
        <LinkText
          text={name}
          onClick={() => {
            navigateToPinpoint({
              objectId: record.objectId,
              pinpoint: record.pinpoint,
              commentId: record.id,
              newTab: true,
            });
          }}
          showIcon
        />
      ) : (
        <Text
          text={t('comment.deleted-with-object', {
            object: CommentKeyLabelModifier[record.key],
          })}
          color="tertiary"
          weight="regular"
        />
      )}
    </Flex>
  );
};
